/**
 * Mehrsprachigkeit: Office-Anzeigesprache (Office.context.displayLanguage) wird erkannt.
 * Deutsch: de, de-DE, de-CH, de-AT, … (alle de-*). Sonst Englisch (Fallback).
 */

declare const Office: { context?: { displayLanguage?: string } } | undefined;

export type Locale = "de" | "en";

export const translations: Record<Locale, Record<string, string>> = {
  de: {
    // Consent
    "consent.title": "Anmeldung",
    "consent.value1": "Erstellen Sie HTML-Signaturen, fügen Sie sie per Klick in neue E-Mails ein und legen Sie eine Standardsignatur fest – alles direkt in Outlook.",
    "consent.value2": "Mit Ihrem Microsoft-Konto verbinden wir Ihre Signaturen sicher mit SunnySign; nach der Anmeldung stehen alle Funktionen bereit.",
    "consent.text": "SunnySign benötigt Ihre Anmeldung und Zustimmung, um Signaturen mit Ihrem Konto zu verknüpfen.",
    "consent.button": "Anmelden & Zustimmung erteilen",
    "consent.buttonLoginOnly": "Erneut anmelden",
    "consent.loginAsNameAgain": "Als {name} erneut anmelden",
    "consent.loginAsNameConsent": "Als {name} anmelden & Zustimmung erteilen",
    "consent.otherAccountButton": "Mit einem anderen Konto anmelden",
    "consent.tutorialAria": "Einführung erneut anzeigen",
    // List
    "list.title": "Signaturen",
    "list.addButton": "Neue Signatur",
    "list.adminPortal": "Admin-Portal",
    "list.adminPortalAria": "Admin-Portal öffnen",
    "list.loading": "Signaturen werden geladen\u2026",
    "campaigns.loading": "Kampagnen werden geladen\u2026",
    "list.empty": "Keine Signaturen vorhanden. Klicken Sie auf „Neue Signatur“, um eine zu erstellen.",
    "list.edit": "Bearbeiten",
    "list.delete": "Löschen",
    "list.use": "Verwenden",
    "list.notTargeted": "Nur für zugeordnete Entra-Gruppen – für Sie nicht verwendbar.",
    "list.menu": "Menü",
    "list.noLicense": "Keine Lizenz — Bitte kontaktieren Sie Ihren Administrator",
    // Campaigns
    "campaigns.title": "Kampagnen",
    "campaigns.manageButton": "Kampagnen verwalten",
    "campaigns.backToSignatures": "← Signaturen",
    "campaigns.addButton": "Neue Kampagne",
    "audience.titleCount": "Verfügbarkeit ({count})",
    "audience.all": "Alle",
    "audience.group": "Gruppe",
    "audience.selectGroups": "Gruppe auswählen",
    "audience.selectGroupButton": "Gruppe auswählen",
    "audience.searchGroups": "Gruppen suchen …",
    "audience.groupsFound": "{count} Gruppen gefunden",
    "audience.confirm": "Bestätigen",
    "audience.checkUsers": "Benutzerliste prüfen",
    "audience.allUsersHint":
      "Ohne Gruppenauswahl gilt der Inhalt für alle lizenzierten Benutzer. Dafür lässt sich keine endliche Liste anzeigen — wähle zuerst mindestens eine Gruppe aus.",
    "audience.searchUsers": "Benutzer suchen …",
    "audience.usersFound": "{count} Benutzer gefunden",
    "audience.loadMore": "Weitere laden",
    "audience.orgDefaultNewMail": "Standard neue E-Mail",
    "audience.orgDefaultReplyForward": "Standard Antwort & Weiterleitung",
    "campaigns.newTitle": "Neue Kampagne",
    "campaigns.editTitle": "Kampagne bearbeiten",
    "campaigns.labelName": "NAME",
    "campaigns.labelStarts": "START (optional)",
    "campaigns.labelEnds": "ENDE (optional)",
    "campaigns.labelContent": "INHALT",
    "campaigns.placeholderContent": "Kampagnen-Inhalt eingeben…",
    "campaigns.save": "Speichern",
    "campaigns.cancel": "Abbrechen",
    "campaigns.empty": "Keine Kampagnen vorhanden.",
    "campaigns.nameRequired": "Bitte einen Namen eingeben.",
    "campaigns.created": "Kampagne erstellt.",
    "campaigns.saved": "Kampagne gespeichert.",
    "campaigns.deleted": "Kampagne gelöscht.",
    "campaigns.deleteConfirm": "Diese Kampagne wirklich löschen?",
    "campaigns.loadFailed": "Kampagnen konnten nicht geladen werden",
    "campaigns.saveFailed": "Speichern fehlgeschlagen",
    "campaigns.deleteFailed": "Löschen fehlgeschlagen",
    "campaigns.signatureSummary": "Kampagnen: {count} aktiv",
    "campaigns.showMappings": "Verwalten",
    "campaigns.signatureDialogTitle": "Kampagnenzuweisung",
    "campaigns.mappingDialogLoading": "Kampagnen werden geladen…",
    "campaigns.signatureDialogHint": "Wähle die Kampagnen aus, die unter dieser Signatur erscheinen sollen.",
    "campaigns.signatureDialogCount": "{selected} von {total} Kampagnen ausgewählt",
    "campaigns.manageSignatures": "Signaturen verwalten",
    "campaigns.campaignSignatureDialogTitle": "Signaturzuweisung – {name}",
    "campaigns.campaignSignatureDialogHint": "Wähle die Signaturen aus, für die diese Kampagne gelten soll.",
    "campaigns.campaignSignatureDialogCount": "{selected} von {total} Signaturen ausgewählt",
    "campaigns.mappingDialogLoadingSignatures": "Signaturen werden geladen…",
    "campaigns.mappingFilter": "Filter",
    "campaigns.mappingFilterSelection": "Auswahl",
    "campaigns.mappingFilterStatus": "Status",
    "campaigns.mappingFilterAll": "Alle",
    "campaigns.mappingFilterSelectedOnly": "Nur ausgewählt",
    "campaigns.mappingFilterUnselectedOnly": "Nur nicht ausgewählt",
    "campaigns.mappingFilterAllStatus": "Alle Status",
    "campaigns.mappingSelectAllVisible": "Alle",
    "campaigns.statusActive": "Aktiv",
    "campaigns.statusPlanned": "Geplant",
    "campaigns.statusInactive": "Inaktiv",
    "campaigns.mappingSignaturesTitle": "Signaturen ({count})",
    "campaigns.mappingSignaturesHint": "Wähle die Signaturen aus, für die diese Kampagne gelten soll.",
    "campaigns.mappingSearchSignatures": "Signaturen suchen…",
    "campaigns.mappingTabAll": "Alle Signaturen ({count})",
    "campaigns.mappingTabSelected": "Ausgewählt ({count})",
    "campaigns.mappingTabUnselected": "Nicht ausgewählt ({count})",
    "campaigns.mappingEmpty": "Keine Signaturen gefunden.",
    "campaigns.mappingSelectedCount": "{selected} von {total} Signaturen ausgewählt",
    "campaigns.mappingSelectAll": "Alle auswählen",
    "campaigns.mappingDeselectAll": "Alle abwählen",
    "campaigns.mappingSignaturesInfo": "Hinweis: Die Kampagne wird in allen ausgewählten Signaturen eingefügt.",
    "campaigns.mappingCampaignsTitle": "Kampagnen ({count})",
    "campaigns.mappingSearchCampaigns": "Kampagnen suchen…",
    "campaigns.mappingTabAllCampaigns": "Alle Kampagnen ({count})",
    "campaigns.mappingEmptyCampaigns": "Keine Kampagnen gefunden.",
    "campaigns.mappingSelectedCountCampaigns": "{selected} von {total} Kampagnen ausgewählt",
    "campaigns.mappingCampaignsInfo": "Hinweis: Ausgewählte Kampagnen werden unter der Signatur eingefügt.",
    "campaigns.mappingTooLong": "Kombinierte Länge Signatur+Kampagne (~{len}) übersteigt das Slot-Limit ({max}). Mapping nicht speicherbar — Inhalte kürzen oder weniger zuweisen.",
    "campaigns.mappingLengthWarn": "Kombinierte Länge (~{len}/{max}) nähert sich dem Slot-Limit.",
    "campaigns.mappingSignatureRequired": "Bitte mindestens eine Signatur auswählen.",
    "campaigns.mappingLoadFailed": "Zuordnung konnte nicht geladen werden",
    "campaigns.mappingSaveFailed": "Zuordnung konnte nicht gespeichert werden",
    "campaigns.mappingSaved": "Zuordnung gespeichert.",
    // Form
    "form.newTitle": "Neue Signatur",
    "form.editTitle": "Signatur bearbeiten",
    "form.labelName": "NAME",
    "form.labelSignature": "SIGNATUR",
    "form.placeholderName": "Signatur 1",
    "form.placeholderContent": "Mit freundlichen Grüßen\n...",
    "form.create": "Erstellen",
    "form.save": "Speichern",
    "form.cancel": "Abbrechen",
    "form.loading": "Wird geladen…",
    "form.consentBannerText": "Profilfelder (z. B. Vorname, Nachname) benötigen eine einmalige Zustimmung. Sie können „Zustimmung erteilen“ (nur für Sie) oder Ihr Administrator kann die App für die Organisation freigeben.",
    "form.consentBannerButton": "Zustimmung erteilen",
    "form.consentGrantedHint": "Zustimmung erteilt. Sie können die Profilfelder nun nutzen.",
    // Preview
    "preview.title": "Signatur-Vorschau",
    "preview.insert": "In E-Mail einfügen",
    "preview.back": "Zurück zur Liste",
    // Delete dialog
    "delete.confirm": "Möchten Sie diese Signatur wirklich löschen?",
    "delete.yes": "Ja, löschen",
    "delete.no": "Abbrechen",
    // Editor toolbar
    "editor.font": "Schriftart",
    "editor.fontSearch": "Schriftart suchen …",
    "editor.mixedFormatting": "Markierung enthält unterschiedliche Formatierungen",
    "editor.size": "Schriftgröße",
    "editor.sizeCustom": "Eigene",
    "editor.lineHeight": "Zeilenabstand",
    "editor.lineHeightCustom": "Eigener",
    "editor.lineHeightInvalid": "Bitte einen Wert zwischen 0,5 und 5 mit maximal zwei Nachkommastellen eingeben.",
    "editor.color": "Schriftfarbe",
    "editor.colorOk": "OK",
    "editor.opacity": "Deckkraft",
    "editor.colorTransparent": "Transparent (keine Farbe)",
    "editor.bgColor": "Hintergrundfarbe",
    "editor.bold": "Fett",
    "editor.italic": "Kursiv",
    "editor.underline": "Unterstrichen",
    "editor.alignLeft": "Linksbündig",
    "editor.alignCenter": "Zentriert",
    "editor.alignRight": "Rechtsbündig",
    "editor.formattingMarks": "Absatzmarken und Leerzeichen ein- oder ausblenden",
    "editor.undo": "Rückgängig",
    "editor.redo": "Wiederherstellen",
    "editor.htmlView": "HTML",
    "editor.htmlViewTitle": "HTML-Ansicht ein- oder ausschalten",
    "editor.link": "Link",
    "editor.linkTitle": "Link",
    "editor.linkScheme": "Typ",
    "editor.linkTarget": "Ziel",
    "editor.linkTargetWeb": "example.com/pfad",
    "editor.linkTargetMail": "name@firma.de",
    "editor.linkTargetPhone": "+493012345678",
    "editor.linkProfileInsert": "Einfügen",
    "editor.linkProfileHint":
      "Profilfeld ins Ziel einfügen – mit jedem Typ, z. B. „mailto:“ + mail, „tel:“ + mobilePhone oder „https://“ + mySite. Beim Einfügen der Signatur wird der Platzhalter durch deinen Wert ersetzt.",
    "editor.linkRequired": "Bitte ein Ziel eingeben.",
    "editor.linkBlocked":
      "Dieser Link ist nicht erlaubt. Erlaubt sind http://, https://, mailto: und tel:.",
    "editor.contentSanitized":
      "Unerlaubte Links oder Bildquellen wurden entfernt. Erlaubt sind http(s), mailto:, tel: sowie sichere Bilder.",
    "editor.linkNoStyle": "Kein Stil (nicht blau/unterstrichen)",
    "editor.hr": "Trennlinie",
    "editor.hrColor": "Trennlinie – Farbe",
    "editor.image": "Bild einfügen",
    "editor.imageUrlPrompt": "Bild-URL (http/https):",
    "editor.imageUrlInvalid": "Bitte nur gültige http-/https-URLs oder eine Bilddatei eingeben.",
    "editor.imageFilePrompt": "Oder Datei (PNG/JPEG/GIF, max. 200 KB):",
    "editor.imageFilePromptOnly": "Bilddatei (PNG/JPEG/GIF, max. 200 KB):",
    "editor.imageFileHint": "Datei wird als Base64 eingebettet. Bei großen Bildern ggf. Einfügemodus „Beta: manuell“ im Admin-Portal nutzen.",
    "editor.imageFileAttachmentPrompt": "Bilddatei als Outlook-Anhang (Blob):",
    "editor.imageFileAttachmentHint": "Wird im Speicher abgelegt und beim Einfügen als Inline-Attachment (cid) gesetzt. Client-Unterstützung variiert.",
    "editor.imageFileInvalid": "Nur freigegebene Bildformate erlaubt.",
    "editor.imageFileTooLarge": "Bild zu groß (max. 200 KB für Base64).",
    "editor.imageFileTooLargeAttachment": "Bild zu groß (max. 2 MB für Anhänge).",
    "editor.imageUploadFailed": "Bild-Upload fehlgeschlagen.",
    "editor.imageFileRequired": "Bitte eine Bilddatei auswählen.",
    "editor.imageDisabled": "Bilder einfügen ist für diesen Mandanten deaktiviert.",
    "editor.imageWidth": "Breite",
    "editor.imageWidthUnit": "Einheit für Breite",
    "editor.imageWidthInvalid": "Breite: ganze Zahl in der gewählten Einheit, z. B. 200 (px) oder 50 (%).",
    "editor.imageHeight": "Höhe",
    "editor.imageHeightUnit": "Einheit für Höhe",
    "editor.imageHeightInvalid": "Höhe: ganze Zahl in der gewählten Einheit, z. B. 100 (px) oder 50 (%).",
    "editor.imageAlign": "Ausrichtung",
    "editor.imageAlignDefault": "Standard (im Textfluss)",
    "editor.profileFields": "Profilfeld",
    "editor.profileSearch": "Profilfeld suchen …",
    "editor.profileFieldsHint":
      "Fügt ein Profilfeld ein, das beim Einfügen der Signatur durch deine Microsoft-365-Daten ersetzt wird. Namen entsprechen Microsoft Graph.",
    "editor.profileFieldItemHint": "Wird beim Einfügen der Signatur durch deine Microsoft-365-Daten ersetzt.",
    "editor.linkPreviewNoValue": "[kein Wert]",
    "editor.profileFieldNoValue": "kein Wert für den aktuellen Benutzer",
    "editor.hideField": "Conditional range",
    "editor.hideFieldHint":
      "Conditional range: markiert den Bereich mit „[#“ und „#]“. Alles zwischen „[#“ und „#]“ wird entfernt, wenn das Profilfeld leer ist. Klick dazu den Platzhalter an oder markiere Text zusammen mit genau einem Platzhalter (z. B. „E-Mail: {{E-Mail}}“).",
    "editor.hideLine": "Hide line",
    "editor.hideLineHint":
      "Hide line: bleibt das Profilfeld leer, entfällt beim Rendern die ganze Zeile samt Beschriftung davor (z. B. „Tel.: “). Schaltet die optionale Endung „|HL“ um. Nur bei einem markierten Platzhalter möglich – klick ihn dazu an.",
    "editor.deletePlaceholder": "Delete field",
    "editor.deletePlaceholderHint":
      "Löscht die komplett markierte Einheit: einen Platzhalter ohne Klammerung oder ein ganzes „[# … #]“ samt Inhalt. Ist nur der Platzhalter innerhalb einer Klammerung markiert, klick auf „[#“ oder „#]“, um alles zu markieren.",
    // Tabellen
    "table.insert": "Tabelle einfügen",
    "table.insertTitle": "Neue Tabelle",
    "table.insertFailed": "Die Tabelle konnte nicht eingefügt werden. Bitte in den Editor klicken und erneut versuchen.",
    "table.section": "Tabelle",
    "table.formatTitle": "Tabelle formatieren",
    "table.formatHeading": "Format",
    "table.scope": "Bereich",
    "table.scopeCell": "Zelle",
    "table.scopeRow": "Zeile",
    "table.scopeColumn": "Spalte",
    "table.scopeTable": "Tabelle",
    "table.rows": "Zeilen",
    "table.columns": "Spalten",
    "table.width": "Breite",
    "table.widthUnit": "Einheit für Breite",
    "table.widthHint": "Zahl in der gewählten Einheit, z. B. 480 (px) oder 50 (%).",
    "table.widthRowHint": "Zeilen haben keine eigene Breite. Nutze „Spalte“ oder „Tabelle“.",
    "table.height": "Höhe",
    "table.heightUnit": "Einheit für Höhe",
    "table.heightAuto": "leer = automatisch",
    "table.borderWidth": "Linienstärke (px)",
    "table.borderStyle": "Linienstil",
    "table.borderStyle.solid": "durchgezogen",
    "table.borderStyle.dashed": "gestrichelt",
    "table.borderStyle.dotted": "gepunktet",
    "table.borderStyle.double": "doppelt",
    "table.borderStyle.none": "keine",
    "table.borderColor": "Linienfarbe",
    "table.borderEdges": "Rahmenlinien",
    "table.borderDetails": "Details …",
    "table.borderDetailsTitle": "Rahmenlinien",
    "table.borderDetailsHint":
      "Linienstil, Farbe und Breite wählen, dann je Kante setzen, entfernen oder unverändert lassen.",
    "table.edgeTop": "Oben",
    "table.edgeBottom": "Unten",
    "table.edgeLeft": "Links",
    "table.edgeRight": "Rechts",
    "table.edgeInsideVertical": "Innen senkrecht",
    "table.edgeInsideHorizontal": "Innen waagerecht",
    "table.edgeUnavailable": "Im gewählten Bereich nicht vorhanden",
    "table.borderStateOn": "Linie",
    "table.borderStateOff": "Keine Linie",
    "table.borderStateKeep": "Unverändert",
    "table.borderStateHint": "Klicken wechselt: unverändert → Linie → keine Linie",
    "table.padding": "Innenabstand (px)",
    "table.background": "Hintergrundfarbe",
    "table.backgroundNone": "Ohne",
    "table.textColor": "Schriftfarbe",
    "table.font": "Schriftart",
    "table.fontSize": "Schriftgröße (px)",
    "table.bold": "Fett",
    "table.italic": "Kursiv",
    "table.underline": "Unterstrichen",
    "table.align": "Ausrichtung",
    "table.valign": "Vertikale Ausrichtung",
    "table.valignTop": "oben",
    "table.valignMiddle": "mittig",
    "table.valignBottom": "unten",
    "table.headerRow": "Erste Zeile als Kopfzeile",
    "table.rowAbove": "Zeile darüber einfügen",
    "table.rowBelow": "Zeile darunter einfügen",
    "table.columnLeft": "Spalte links einfügen",
    "table.columnRight": "Spalte rechts einfügen",
    "table.deleteRow": "Zeile löschen",
    "table.deleteColumn": "Spalte löschen",
    "table.mergeCells": "Zellen verbinden",
    "table.splitCells": "Zellen teilen",
    "table.selectCellsHint": "Zellen mit Umschalt + Klick oder Ziehen auswählen",
    "table.deleteTable": "Tabelle löschen",
    "table.errorRows": "Zeilen: ganze Zahl zwischen 1 und 50.",
    "table.errorColumns": "Spalten: ganze Zahl zwischen 1 und 20.",
    "table.errorBorderWidth": "Linienstärke: ganze Zahl zwischen 0 und 20 (px).",
    "table.errorPadding": "Innenabstand: ganze Zahl zwischen 0 und 60 (px).",
    "table.errorFontSize": "Schriftgröße: ganze Zahl zwischen 1 und 200 (px).",
    "table.errorWidth": "Breite: ganze Zahl in der gewählten Einheit, z. B. 480 (px) oder 50 (%).",
    "table.errorHeight": "Höhe: ganze Zahl in der gewählten Einheit, z. B. 40. Leer lässt die Höhe automatisch.",
    // Messages
    "msg.outlookOnly": "Dieses Add-In funktioniert nur in Outlook.",
    "msg.authFailed": "Anmeldung fehlgeschlagen. Bitte erneut versuchen.",
    "msg.ssoFailed": "Automatische Anmeldung fehlgeschlagen. Bitte melden Sie sich manuell an.",
    "msg.ssoOtherAccountHelp":
      "Wechseln oder fügen Sie das gewünschte Konto in Outlook hinzu (z. B. über Ihr Profil bzw. Datei → Konto) und tippen Sie erneut auf „Mit einem anderen Konto anmelden“. Es wird kein Browser geöffnet.",
    "msg.sessionExpired": "Sitzung abgelaufen. Bitte melden Sie sich erneut an.",
    "msg.loadError": "Fehler beim Laden der Signaturen (Status: {status}). Stellen Sie sicher, dass das Backend auf {apiUrl} läuft. Details: {details}",
    "msg.deleteSuccess": "Signatur erfolgreich gelöscht.",
    "msg.deleteError": "Fehler beim Löschen der Signatur.",
    "msg.fillAll": "Bitte füllen Sie alle Felder aus.",
    "msg.lengthWarn": "Signatur wird groß ({len} Zeichen). Speichern ist ab {saveMax} deaktiviert (Office.js-Slot: {max}).",
    "msg.tooLong": "Signatur zu lang ({len} Zeichen). Speichern erst unter {saveMax} Zeichen möglich (Office.js-Slot: {max}). Bitte Bilder als URL oder Anhang (cid) nutzen.",
    "msg.mappingBlocked": "Zuordnung Signatur↔Kampagne überschreitet das Slot-Limit (~{len}/{max}). Bitte Inhalte kürzen oder weniger Kampagnen zuweisen.",
    "msg.updateSuccess": "Signatur erfolgreich aktualisiert.",
    "msg.createSuccess": "Signatur erfolgreich erstellt.",
    "msg.saveError": "Fehler beim Speichern der Signatur.",
    "msg.selectFirst": "Bitte wählen Sie zuerst eine Signatur aus.",
    "msg.notTargeted": "Diese Signatur ist nur für bestimmte Entra-Gruppen freigegeben und kann von Ihnen nicht eingefügt werden.",
    "msg.defaultNotTargeted": "Die Standardsignatur ist nur für bestimmte Entra-Gruppen freigegeben – bitte wählen Sie eine andere Standardsignatur.",
    "msg.replyForwardDefaultNotTargeted": "Die Antwort-/Weiterleitungs-Signatur ist nur für bestimmte Entra-Gruppen freigegeben – bitte wählen Sie eine andere Signatur.",
    "msg.insertSuccess": "Signatur erfolgreich eingefügt!",
    "msg.insertError": "Fehler beim Einfügen der Signatur: {error}",
    "msg.openEmail": "Bitte öffnen Sie eine E-Mail, um die Signatur einzufügen.",
    "msg.insertGenericError": "Fehler beim Einfügen der Signatur.",
    "settings.defaultSignatureLabel": "Standardsignatur:",
    "settings.newMailDefaultSignatureLabel": "Neue E-Mail:",
    "settings.replyForwardDefaultSignatureLabel": "Antwort & Weiterleitung:",
    "settings.defaultSignatureNone": "— keine —",
    "settings.orgDefaultBadge": "Unternehmens",
    "settings.openAria": "Einstellungen öffnen",
    "settings.backAria": "Zurück",
    "settings.backLabel": "Zurück",
    "settings.pageAria": "Einstellungen",
    "settings.mobileOnlyInsert": "Nur bei Mobile einfügen",
    "settings.showTaskpaneByDefault": "Add-in standardmäßig bei neuer E-Mail anzeigen",
    "settings.pinHint": "Damit SunnySign bei jeder neuen E-Mail sichtbar ist (klassisches & neues Outlook): Taskpane oben anheften (Pin-Symbol).",
    // Header / Auth
    "header.logout": "Abmelden",
    "header.logoutConfirm": "Möchten Sie sich wirklich abmelden?",
    "header.logoutYes": "Ja",
    "header.logoutNo": "Nein",
    // First-run carousel (FRE)
    "fre.skip": "Überspringen",
    "fre.next": "Weiter",
    "fre.back": "Zurück",
    "fre.getStarted": "Loslegen",
    "fre.ariaCarousel": "Kurze Einführung in SunnySign",
    "fre.dotsAria": "Folien",
    "fre.dotLabel": "Folie {n} von {total}",
    "fre.proRequired": "Pro",
    "fre.proRequiredNote": "Ausschließlich Pro – Upgrade nötig.",
    "fre.proExclusiveNote": "Ausschließlich ein Pro-Feature.",
    "fre.slide.start.title": "Meine Signaturen",
    "fre.slide.start.body":
      "Auf der Startseite sehen Sie Ihre Signaturen und legen mit „Neue Signatur“ los. Mit „Verwenden“ fügen Sie eine Signatur direkt in die E-Mail ein.",
    "fre.slide.menu.title": "Signaturen verwalten",
    "fre.slide.menu.body":
      "Über das Menü (⋯) einer Signatur bearbeiten oder löschen. Mit „Verwenden“ erscheint die Signatur im E-Mail-Text. In Pro sehen und nutzen nur Mitglieder der Editoren-Gruppe die drei Punkte – die Gruppe wird in den Admin-Einstellungen festgelegt.",
    "fre.slide.editor.title": "Signatur im Editor gestalten",
    "fre.slide.editor.body":
      "Gestalten Sie Ihre HTML-Signatur mit Formatierung, Bildern und Links. Unten finden Sie Abschnitte für Verfügbarkeit und Kampagnen (Pro).",
    "fre.slide.profile.title": "Profilfelder nutzen",
    "fre.slide.profile.body":
      "Fügen Sie Platzhalter für Profildaten ein (z. B. Name, Telefon). „Hide line“ blendet die ganze Zeile aus, wenn der Wert fehlt. „Conditional range“ umschließt Text mit [# … #] – der markierte Block (z. B. „E-Mail: {{mail}}“) erscheint nur, wenn das Profilfeld einen Wert hat.",
    "fre.slide.availability.title": "Verfügbarkeit steuern",
    "fre.slide.availability.body":
      "Hier legen Sie organisationsweit fest, für wen die Signatur gilt (Alle oder Entra-Gruppe) und ob sie als Standardsignatur für neue Mails bzw. Antworten & Weiterleitungen gilt.",
    "fre.slide.campaigns.title": "Kampagnen zuweisen",
    "fre.slide.campaigns.body":
      "Weisen Sie Kampagnen einer Signatur zu – zeitgesteuerte Banner erscheinen unter der Signatur.",
    "fre.slide.defaults.title": "Standardsignatur festlegen",
    "fre.slide.defaults.body":
      "In den Einstellungen wählen Sie Standardsignaturen für neue Mails sowie Antworten & Weiterleitungen. Organisations-Signaturen (Badge) sind Pro: Wird die Organisations-Signatur zentral geändert, übernimmt Ihre Auswahl automatisch die neue Version – und neue Benutzer erhalten sie von Anfang an.",
    "fre.slide.pin.title": "Add-In anpinnen",
    "fre.slide.pin.body":
      "Heften Sie den Aufgabenbereich mit dem Pin-Symbol an, damit SunnySign beim Verfassen schnell griffbereit bleibt.",
    // Premium
    "premium.title": "Premium",
    "premium.sneakPeek": "Sneak Peek – mit Premium freischaltbar",
    "premium.campaigns": "Kampagnen",
    "premium.campaigns.desc": "Signatur-Banner für Aktionen zeitgesteuert einbinden",
    "premium.organisation": "Signatur nach Organisation",
    "premium.organisation.desc": "Signaturen Standort, Abteilung oder Firma zuweisen",
    "premium.admin": "Zentrale Verwaltung",
    "premium.admin.desc": "Vorlagen zentral vorgeben, Nutzer wählen nur aus",
    "premium.templates": "Vorlagen-Bibliothek",
    "premium.analytics": "Analytics & Reporting",
    "premium.compliance": "Compliance & Rechtstexte",
    "premium.customFields": "Eigene Felder",
    "premium.approval": "Genehmigungs-Workflow",
    "premium.whitelabel": "White-Label",
    "premium.multitenant": "Mehrere Firmen",
    "premium.learnMore": "Mehr erfahren",
    "premium.formTeaserTitle": "Premium in der Bearbeitung",
    "premium.heroLine": "Mehr Power für Ihre Signaturen – mit SunnySign Pro.",
    "premium.discover": "Pro entdecken",
    "premium.upgradeToPro": "Upgrade zu Pro",
    "premium.pillOrgTemplates": "Organisations-Vorlagen",
    "premium.pillOrgDefaults": "Organisations-Standardsignaturen",
    "premium.pillTargetGroups": "Zielgruppen (Entra-Gruppen)",
    "premium.pillScim": "SCIM",
  },
  en: {
    "consent.title": "Sign in",
    "consent.value1": "Create rich HTML signatures, insert them into new emails with one click, and set a default signature—all inside Outlook.",
    "consent.value2": "Signing in with your Microsoft account securely links your signatures to SunnySign so everything stays in sync.",
    "consent.text": "SunnySign requires your sign-in and consent to link signatures with your account.",
    "consent.button": "Sign in & grant consent",
    "consent.buttonLoginOnly": "Sign in again",
    "consent.loginAsNameAgain": "Sign in again as {name}",
    "consent.loginAsNameConsent": "Sign in as {name} & grant consent",
    "consent.otherAccountButton": "Sign in with a different account",
    "consent.tutorialAria": "Show introduction again",
    "list.title": "Signatures",
    "list.addButton": "New signature",
    "list.adminPortal": "Admin portal",
    "list.adminPortalAria": "Open admin portal",
    "list.loading": "Loading signatures\u2026",
    "campaigns.loading": "Loading campaigns\u2026",
    "list.empty": "No signatures yet. Click \"New signature\" to create one.",
    "list.edit": "Edit",
    "list.delete": "Delete",
    "list.use": "Use",
    "list.notTargeted": "Limited to the assigned Entra groups – not available for you.",
    "list.menu": "Menu",
    "list.noLicense": "No license — please contact your administrator",
    "campaigns.title": "Campaigns",
    "campaigns.manageButton": "Manage campaigns",
    "campaigns.backToSignatures": "← Signatures",
    "campaigns.addButton": "New campaign",
    "audience.titleCount": "Availability ({count})",
    "audience.all": "All",
    "audience.group": "Group",
    "audience.selectGroups": "Select group",
    "audience.selectGroupButton": "Select group",
    "audience.searchGroups": "Search groups …",
    "audience.groupsFound": "{count} groups found",
    "audience.confirm": "Confirm",
    "audience.checkUsers": "Check user list",
    "audience.allUsersHint":
      "Without a group selection the content applies to all licensed users. No finite list can be shown for that — select at least one group first.",
    "audience.searchUsers": "Search users …",
    "audience.usersFound": "{count} users found",
    "audience.loadMore": "Load more",
    "audience.orgDefaultNewMail": "Default new mail",
    "audience.orgDefaultReplyForward": "Default reply & forward",
    "campaigns.newTitle": "New campaign",
    "campaigns.editTitle": "Edit campaign",
    "campaigns.labelName": "NAME",
    "campaigns.labelStarts": "START (optional)",
    "campaigns.labelEnds": "END (optional)",
    "campaigns.labelContent": "CONTENT",
    "campaigns.placeholderContent": "Enter campaign content…",
    "campaigns.save": "Save",
    "campaigns.cancel": "Cancel",
    "campaigns.empty": "No campaigns yet.",
    "campaigns.nameRequired": "Please enter a name.",
    "campaigns.created": "Campaign created.",
    "campaigns.saved": "Campaign saved.",
    "campaigns.deleted": "Campaign deleted.",
    "campaigns.deleteConfirm": "Delete this campaign?",
    "campaigns.loadFailed": "Could not load campaigns",
    "campaigns.saveFailed": "Save failed",
    "campaigns.deleteFailed": "Delete failed",
    "campaigns.signatureSummary": "Campaigns: {count} active",
    "campaigns.showMappings": "Manage",
    "campaigns.signatureDialogTitle": "Campaign assignment",
    "campaigns.mappingDialogLoading": "Loading campaigns…",
    "campaigns.signatureDialogHint": "Select the campaigns that should appear under this signature.",
    "campaigns.signatureDialogCount": "{selected} of {total} campaigns selected",
    "campaigns.manageSignatures": "Manage signatures",
    "campaigns.campaignSignatureDialogTitle": "Signature assignment – {name}",
    "campaigns.campaignSignatureDialogHint": "Select the signatures this campaign should apply to.",
    "campaigns.campaignSignatureDialogCount": "{selected} of {total} signatures selected",
    "campaigns.mappingDialogLoadingSignatures": "Loading signatures…",
    "campaigns.mappingFilter": "Filter",
    "campaigns.mappingFilterSelection": "Selection",
    "campaigns.mappingFilterStatus": "Status",
    "campaigns.mappingFilterAll": "All",
    "campaigns.mappingFilterSelectedOnly": "Selected only",
    "campaigns.mappingFilterUnselectedOnly": "Not selected only",
    "campaigns.mappingFilterAllStatus": "All statuses",
    "campaigns.mappingSelectAllVisible": "All",
    "campaigns.statusActive": "Active",
    "campaigns.statusPlanned": "Planned",
    "campaigns.statusInactive": "Inactive",
    "campaigns.mappingSignaturesTitle": "Signatures ({count})",
    "campaigns.mappingSignaturesHint": "Select the signatures this campaign should apply to.",
    "campaigns.mappingSearchSignatures": "Search signatures…",
    "campaigns.mappingTabAll": "All signatures ({count})",
    "campaigns.mappingTabSelected": "Selected ({count})",
    "campaigns.mappingTabUnselected": "Not selected ({count})",
    "campaigns.mappingEmpty": "No signatures found.",
    "campaigns.mappingSelectedCount": "{selected} of {total} signatures selected",
    "campaigns.mappingSelectAll": "Select all",
    "campaigns.mappingDeselectAll": "Deselect all",
    "campaigns.mappingSignaturesInfo": "Note: The campaign will be inserted into all selected signatures.",
    "campaigns.mappingCampaignsTitle": "Campaigns ({count})",
    "campaigns.mappingSearchCampaigns": "Search campaigns…",
    "campaigns.mappingTabAllCampaigns": "All campaigns ({count})",
    "campaigns.mappingEmptyCampaigns": "No campaigns found.",
    "campaigns.mappingSelectedCountCampaigns": "{selected} of {total} campaigns selected",
    "campaigns.mappingCampaignsInfo": "Note: Selected campaigns will appear below the signature.",
    "campaigns.mappingTooLong": "Combined signature+campaign length (~{len}) exceeds the slot limit ({max}). Mapping cannot be saved — shorten content or assign fewer items.",
    "campaigns.mappingLengthWarn": "Combined length (~{len}/{max}) is approaching the slot limit.",
    "campaigns.mappingSignatureRequired": "Please select at least one signature.",
    "campaigns.mappingLoadFailed": "Could not load mapping",
    "campaigns.mappingSaveFailed": "Could not save mapping",
    "campaigns.mappingSaved": "Mapping saved.",
    "form.newTitle": "New signature",
    "form.editTitle": "Edit signature",
    "form.labelName": "NAME",
    "form.labelSignature": "SIGNATURE",
    "form.placeholderName": "Signature 1",
    "form.placeholderContent": "Kind regards\n...",
    "form.create": "Create",
    "form.save": "Save",
    "form.cancel": "Cancel",
    "form.loading": "Loading…",
    "form.consentBannerText": "Profile fields (e.g. first name, last name) require one-time consent. You can grant consent (for yourself only) or your admin can approve the app for your organization.",
    "form.consentBannerButton": "Grant consent",
    "form.consentGrantedHint": "Consent granted. You can now use profile fields.",
    "preview.title": "Signature preview",
    "preview.insert": "Insert into email",
    "preview.back": "Back to list",
    "delete.confirm": "Do you really want to delete this signature?",
    "delete.yes": "Yes, delete",
    "delete.no": "Cancel",
    "editor.font": "Font",
    "editor.fontSearch": "Search fonts …",
    "editor.mixedFormatting": "Selection contains mixed formatting",
    "editor.size": "Font size",
    "editor.sizeCustom": "Custom",
    "editor.lineHeight": "Line spacing",
    "editor.lineHeightCustom": "Custom",
    "editor.lineHeightInvalid": "Enter a value between 0.5 and 5 with no more than two decimal places.",
    "editor.color": "Text color",
    "editor.colorOk": "OK",
    "editor.opacity": "Opacity",
    "editor.colorTransparent": "Transparent (no fill)",
    "editor.bgColor": "Background color",
    "editor.bold": "Bold",
    "editor.italic": "Italic",
    "editor.underline": "Underline",
    "editor.alignLeft": "Align left",
    "editor.alignCenter": "Center",
    "editor.alignRight": "Align right",
    "editor.formattingMarks": "Show or hide paragraph marks and spaces",
    "editor.undo": "Undo",
    "editor.redo": "Redo",
    "editor.htmlView": "HTML",
    "editor.htmlViewTitle": "Toggle HTML source view",
    "editor.link": "Link",
    "editor.linkTitle": "Link",
    "editor.linkScheme": "Type",
    "editor.linkTarget": "Target",
    "editor.linkTargetWeb": "example.com/path",
    "editor.linkTargetMail": "name@company.com",
    "editor.linkTargetPhone": "+493012345678",
    "editor.linkProfileInsert": "Insert",
    "editor.linkProfileHint":
      "Insert a profile field into the target – with any type, e.g. “mailto:” + mail, “tel:” + mobilePhone, or “https://” + mySite. When the signature is inserted, the placeholder is replaced with your value.",
    "editor.linkRequired": "Please enter a target.",
    "editor.linkBlocked":
      "This link is not allowed. Use http://, https://, mailto:, or tel:.",
    "editor.contentSanitized":
      "Disallowed links or image sources were removed. Allowed: http(s), mailto:, tel:, and safe images.",
    "editor.linkNoStyle": "No style (not blue/underlined)",
    "editor.hr": "Horizontal rule",
    "editor.hrColor": "Horizontal rule color",
    "editor.image": "Insert image",
    "editor.imageUrlPrompt": "Image URL (http/https):",
    "editor.imageUrlInvalid": "Please enter a valid http/https URL or choose an image file.",
    "editor.imageFilePrompt": "Or file (PNG/JPEG/GIF, max. 200 KB):",
    "editor.imageFilePromptOnly": "Image file (PNG/JPEG/GIF, max. 200 KB):",
    "editor.imageFileHint": "File is embedded as Base64. For large images, consider Admin setting “Beta: manual insert”.",
    "editor.imageFileAttachmentPrompt": "Image file as Outlook attachment (blob):",
    "editor.imageFileAttachmentHint": "Stored in blob storage and attached inline (cid) on insert. Client support varies.",
    "editor.imageFileInvalid": "Only approved image formats are allowed.",
    "editor.imageFileTooLarge": "Image too large (max. 200 KB for Base64).",
    "editor.imageFileTooLargeAttachment": "Image too large (max. 2 MB for attachments).",
    "editor.imageUploadFailed": "Image upload failed.",
    "editor.imageFileRequired": "Please select an image file.",
    "editor.imageDisabled": "Inserting images is disabled for this tenant.",
    "editor.imageWidth": "Width",
    "editor.imageWidthUnit": "Unit for width",
    "editor.imageWidthInvalid": "Width: whole number in the selected unit, e.g. 200 (px) or 50 (%).",
    "editor.imageHeight": "Height",
    "editor.imageHeightUnit": "Unit for height",
    "editor.imageHeightInvalid": "Height: whole number in the selected unit, e.g. 100 (px) or 50 (%).",
    "editor.imageAlign": "Alignment",
    "editor.imageAlignDefault": "Default (inline)",
    "editor.profileFields": "Profile field",
    "editor.profileSearch": "Search profile fields …",
    "editor.profileFieldsHint":
      "Inserts a profile field that is replaced with your Microsoft 365 data when the signature is inserted. Names match Microsoft Graph.",
    "editor.profileFieldItemHint": "Replaced with your Microsoft 365 data when the signature is inserted.",
    "editor.linkPreviewNoValue": "[no value]",
    "editor.profileFieldNoValue": "no value for current user",
    "editor.hideField": "Conditional range",
    "editor.hideFieldHint":
      "Conditional range: marks the range with \"[#\" and \"#]\". Everything between \"[#\" and \"#]\" is removed if the profile field is empty. Click the placeholder, or select text together with exactly one placeholder (e.g. \"E-mail: {{E-Mail}}\").",
    "editor.hideLine": "Hide line",
    "editor.hideLineHint":
      "Hide line: if the profile field resolves to empty, the whole line (including any leading label like \"Phone: \") is removed on rendering. This toggles the optional \"|HL\" suffix. Works on a selected placeholder only - click it first.",
    "editor.deletePlaceholder": "Delete field",
    "editor.deletePlaceholderHint":
      "Deletes the fully selected unit: a placeholder without brackets, or a whole \"[# ... #]\" including its content. If only the placeholder inside brackets is selected, click \"[#\" or \"#]\" to select everything.",
    "table.insert": "Insert table",
    "table.insertTitle": "New table",
    "table.insertFailed": "The table could not be inserted. Click into the editor and try again.",
    "table.section": "Table",
    "table.formatTitle": "Format table",
    "table.formatHeading": "Format",
    "table.scope": "Scope",
    "table.scopeCell": "Cell",
    "table.scopeRow": "Row",
    "table.scopeColumn": "Column",
    "table.scopeTable": "Table",
    "table.rows": "Rows",
    "table.columns": "Columns",
    "table.width": "Width",
    "table.widthUnit": "Unit for width",
    "table.widthHint": "Number in the selected unit, e.g. 480 (px) or 50 (%).",
    "table.widthRowHint": "Rows have no width of their own. Use \"Column\" or \"Table\".",
    "table.height": "Height",
    "table.heightUnit": "Unit for height",
    "table.heightAuto": "empty = automatic",
    "table.borderWidth": "Border width (px)",
    "table.borderStyle": "Border style",
    "table.borderStyle.solid": "solid",
    "table.borderStyle.dashed": "dashed",
    "table.borderStyle.dotted": "dotted",
    "table.borderStyle.double": "double",
    "table.borderStyle.none": "none",
    "table.borderColor": "Border color",
    "table.borderEdges": "Borders",
    "table.borderDetails": "Details …",
    "table.borderDetailsTitle": "Borders",
    "table.borderDetailsHint":
      "Pick line style, color and width, then set, remove or keep each edge.",
    "table.edgeTop": "Top",
    "table.edgeBottom": "Bottom",
    "table.edgeLeft": "Left",
    "table.edgeRight": "Right",
    "table.edgeInsideVertical": "Inside vertical",
    "table.edgeInsideHorizontal": "Inside horizontal",
    "table.edgeUnavailable": "Not available for the selected scope",
    "table.borderStateOn": "Line",
    "table.borderStateOff": "No line",
    "table.borderStateKeep": "Unchanged",
    "table.borderStateHint": "Click cycles: unchanged → line → no line",
    "table.padding": "Padding (px)",
    "table.background": "Background color",
    "table.backgroundNone": "None",
    "table.textColor": "Text color",
    "table.font": "Font",
    "table.fontSize": "Font size (px)",
    "table.bold": "Bold",
    "table.italic": "Italic",
    "table.underline": "Underline",
    "table.align": "Alignment",
    "table.valign": "Vertical alignment",
    "table.valignTop": "top",
    "table.valignMiddle": "middle",
    "table.valignBottom": "bottom",
    "table.headerRow": "First row as header",
    "table.rowAbove": "Insert row above",
    "table.rowBelow": "Insert row below",
    "table.columnLeft": "Insert column left",
    "table.columnRight": "Insert column right",
    "table.deleteRow": "Delete row",
    "table.deleteColumn": "Delete column",
    "table.mergeCells": "Merge cells",
    "table.splitCells": "Split cells",
    "table.selectCellsHint": "Select cells with Shift + click or drag",
    "table.deleteTable": "Delete table",
    "table.errorRows": "Rows: whole number between 1 and 50.",
    "table.errorColumns": "Columns: whole number between 1 and 20.",
    "table.errorBorderWidth": "Border width: whole number between 0 and 20 (px).",
    "table.errorPadding": "Padding: whole number between 0 and 60 (px).",
    "table.errorFontSize": "Font size: whole number between 1 and 200 (px).",
    "table.errorWidth": "Width: whole number in the selected unit, e.g. 480 (px) or 50 (%).",
    "table.errorHeight": "Height: whole number in the selected unit, e.g. 40. Leave empty for automatic.",
    "msg.outlookOnly": "This add-in only works in Outlook.",
    "msg.authFailed": "Sign-in failed. Please try again.",
    "msg.ssoFailed": "Automatic sign-in failed. Please sign in manually.",
    "msg.ssoOtherAccountHelp":
      "Add or switch to the account you want in Outlook (your profile menu or File → Account), then tap “Sign in with a different account” again. No browser window is used.",
    "msg.sessionExpired": "Session expired. Please sign in again.",
    "msg.loadError": "Error loading signatures (Status: {status}). Make sure the backend is running at {apiUrl}. Details: {details}",
    "msg.deleteSuccess": "Signature deleted successfully.",
    "msg.deleteError": "Error deleting signature.",
    "msg.fillAll": "Please fill in all fields.",
    "msg.lengthWarn": "Signature is getting large ({len} characters). Saving is disabled above {saveMax} (Office.js slot: {max}).",
    "msg.tooLong": "Signature too long ({len} characters). Saving requires under {saveMax} characters (Office.js slot: {max}). Use URL or attachment (cid) images.",
    "msg.mappingBlocked": "Signature↔campaign mapping exceeds the slot limit (~{len}/{max}). Shorten content or assign fewer campaigns.",
    "msg.updateSuccess": "Signature updated successfully.",
    "msg.createSuccess": "Signature created successfully.",
    "msg.saveError": "Error saving signature.",
    "msg.selectFirst": "Please select a signature first.",
    "msg.notTargeted": "This signature is limited to specific Entra groups and cannot be inserted by you.",
    "msg.defaultNotTargeted": "The default signature is limited to specific Entra groups – please choose a different default signature.",
    "msg.replyForwardDefaultNotTargeted": "The reply/forward default signature is limited to specific Entra groups – please choose a different signature.",
    "msg.insertSuccess": "Signature inserted successfully!",
    "msg.insertError": "Error inserting signature: {error}",
    "msg.openEmail": "Please open an email to insert the signature.",
    "msg.insertGenericError": "Error inserting signature.",
    "settings.defaultSignatureLabel": "Default signature:",
    "settings.newMailDefaultSignatureLabel": "New mail:",
    "settings.replyForwardDefaultSignatureLabel": "Reply & forward:",
    "settings.defaultSignatureNone": "— none —",
    "settings.orgDefaultBadge": "Organization",
    "settings.openAria": "Open settings",
    "settings.backAria": "Back",
    "settings.backLabel": "Back",
    "settings.pageAria": "Settings",
    "settings.showTaskpaneByDefault": "Show add-in by default when composing a new email",
    "settings.pinHint": "To have SunnySign visible for every new email (classic & new Outlook): Pin the task pane (pin icon at the top).",
    "header.logout": "Sign out",
    "header.logoutConfirm": "Do you really want to sign out?",
    "header.logoutYes": "Yes",
    "header.logoutNo": "No",
    "fre.skip": "Skip",
    "fre.next": "Next",
    "fre.back": "Back",
    "fre.getStarted": "Get started",
    "fre.ariaCarousel": "Short introduction to SunnySign",
    "fre.dotsAria": "Slides",
    "fre.dotLabel": "Slide {n} of {total}",
    "fre.proRequired": "Pro",
    "fre.proRequiredNote": "Pro only – upgrade required.",
    "fre.proExclusiveNote": "Pro-only feature.",
    "fre.slide.start.title": "My signatures",
    "fre.slide.start.body":
      "On the home page you see your signatures and can start with New signature. Tap Use to insert a signature directly into the email.",
    "fre.slide.menu.title": "Manage signatures",
    "fre.slide.menu.body":
      "Open the menu (⋯) on a signature to edit or delete it. Tap Use to place the signature in the message body. In Pro, only members of the Editors group can see and use the three-dot menu—the group is set in Admin settings.",
    "fre.slide.editor.title": "Design your signature",
    "fre.slide.editor.body":
      "Build your HTML signature with formatting, images, and links. Below the editor you’ll find Availability and Campaigns sections (Pro).",
    "fre.slide.profile.title": "Use profile fields",
    "fre.slide.profile.body":
      "Insert placeholders for profile data (e.g. name, phone). Hide line removes the whole line when the value is empty. Conditional range wraps text in [# … #]—the marked block (e.g. “E-Mail: {{mail}}”) only appears when the profile field has a value.",
    "fre.slide.availability.title": "Control availability",
    "fre.slide.availability.body":
      "Set organization-wide who the signature applies to (Everyone or an Entra group) and whether it is the default for new mail and for replies & forwards.",
    "fre.slide.campaigns.title": "Assign campaigns",
    "fre.slide.campaigns.body":
      "Assign campaigns to a signature—time-based banners appear under the signature.",
    "fre.slide.defaults.title": "Set a default signature",
    "fre.slide.defaults.body":
      "In Settings, pick defaults for new mail and for replies & forwards. Organization signatures (badge) are Pro: if the organization changes that signature, your selection updates automatically—and new users get the organization signature by default.",
    "fre.slide.pin.title": "Pin the add-in",
    "fre.slide.pin.body":
      "Pin the task pane with the pin icon so SunnySign stays handy whenever you write email.",
    // Premium
    "premium.title": "Premium",
    "premium.sneakPeek": "Sneak peek – unlock with Premium",
    "premium.campaigns": "Campaigns",
    "premium.campaigns.desc": "Add time-based signature banners for campaigns",
    "premium.organisation": "Signature by organisation",
    "premium.organisation.desc": "Assign signatures to location, department or company",
    "premium.admin": "Central management",
    "premium.admin.desc": "Admins set templates; users pick from them",
    "premium.templates": "Template library",
    "premium.analytics": "Analytics & Reporting",
    "premium.compliance": "Compliance & legal text",
    "premium.customFields": "Custom fields",
    "premium.approval": "Approval workflow",
    "premium.whitelabel": "White-label",
    "premium.multitenant": "Multiple companies",
    "premium.learnMore": "Learn more",
    "premium.formTeaserTitle": "Premium in editing",
    "premium.heroLine": "More power for your signatures – with SunnySign Pro.",
    "premium.discover": "Discover Pro",
    "premium.upgradeToPro": "Upgrade to Pro",
    "premium.pillOrgTemplates": "Organization templates",
    "premium.pillOrgDefaults": "Organization default signatures",
    "premium.pillTargetGroups": "Target groups (Entra groups)",
    "premium.pillScim": "SCIM",
  },
};

let currentLocale: Locale = "en";

/**
 * Ermittelt die Anzeigesprache aus Office (z. B. "de-DE" → "de", "en-US" → "en").
 * Sollte nach Office.onReady aufgerufen werden.
 */
export function getLocaleFromOffice(): Locale {
  try {
    const displayLanguage =
      (typeof Office !== "undefined" && (Office.context as any).displayLanguage) || "";
    const lower = String(displayLanguage).toLowerCase().trim();
    if (lower === "de" || lower.startsWith("de-")) return "de";
    if (lower === "en" || lower.startsWith("en-")) return "en";
  } catch {
    // ignore
  }
  return "en";
}

export function setLocale(locale: Locale): void {
  currentLocale = locale;
}

export function getLocale(): Locale {
  return currentLocale;
}

/**
 * Übersetzung für den aktuellen Locale. Platzhalter {key} ersetzen mit params.
 * Beispiel: t("msg.loadError", { status: "404", details: "...", apiUrl: API_BASE_URL })
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const str =
    translations[currentLocale][key] ?? translations.en[key] ?? translations.de[key] ?? key;
  if (!params) return str;
  return Object.keys(params).reduce((acc, k) => {
    const val = params[k] ?? "";
    return acc.split(`{${k}}`).join(String(val));
  }, str);
}

